import React from "react";
import PropTypes from "prop-types";
import { Box, Grid, Typography } from "@mui/material";
import SaveButton from "components/buttons/SaveButton";
import assetRequestFields from "config/forms/assetRequestFields";
import CustomTextField from "components/inputs/CustomTextField";
import DropdownField from "components/inputs/DropdownField";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useIsMutating } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import DateField from "components/inputs/DateField";
import AutocompleteMap from "components/maps/AutoCompleteMap";
import AlfrescoFileUploader from "components/fileUploaders/AlfrescoFileUploader";
import FormActions from "components/containers/FormActions";
import ExitFormModal from "components/modals/ExitFormModal";
import { buildDecimalYupSchema } from "utils/decimalFieldHelpers";

const nonFloorOptions = ["Οικόπεδο", "Κοινόχρηστος χώρος / Πάρκο", "Ρέμα"];

const AssetCreateForm = ({ selectedTab, onCreate, selectedAssetCategory }) => {
  const isCreatingLoading =
    useIsMutating({ mutationKey: ["createAsset"] }) +
      useIsMutating({ mutationKey: ["uploadFiles"] }) +
      useIsMutating({ mutationKey: ["deleteMultipleFiles"] }) +
      useIsMutating({ mutationKey: ["deleteFile"] }) >
    0;

  const schema = yup.object(
    Object.keys(assetRequestFields).reduce((acc, key) => {
      assetRequestFields[key].forEach((field) => {
        let fieldSchema;

        if (field.type === "geoLocation") {
          fieldSchema = yup
            .object({
              formattedAddress: yup.string().nullable(),
              lat: yup.number().required("Το πεδίο είναι υποχρεωτικό"),
              lng: yup.number().required("Το πεδίο είναι υποχρεωτικό"),
              postalCode: yup.string().required("Το πεδίο είναι υποχρεωτικό"),
              city: yup.string().required("Το πεδίο είναι υποχρεωτικό"),
            })
            .shape({
              formattedAddress: yup.string().nullable(),
              lat: yup.number().required("Το πεδίο είναι υποχρεωτικό"),
              lng: yup.number().required("Το πεδίο είναι υποχρεωτικό"),
              postalCode: yup.string().required("Το πεδίο είναι υποχρεωτικό"),
              city: yup.string().required("Το πεδίο είναι υποχρεωτικό"),
            });
        } else if (field.type === "text" || field.type === "textarea") {
          fieldSchema = yup.string();
        } else if (field.type === "select" || field.type === "boolean") {
          fieldSchema = yup.string();
        } else if (field.type === "number") {
          fieldSchema = field.isDecimal
            ? buildDecimalYupSchema({
                decimalPlaces: field.decimalPlaces,
                validationMessage: field.validationMessage,
              })
            : yup
                .number()
                .transform((value) => (isNaN(value) ? undefined : value));
        } else if (field.type === "date") {
          fieldSchema = yup.date().transform((value, originalValue) => {
            return isNaN(Date.parse(originalValue)) ? undefined : value;
          });
        } else if (field.type === "file") {
          const validTypes = field.validTypes || [];
          const maxSize = field.maxSize || 5000000;

          fieldSchema = yup.mixed().test({
            name: "fileValidation",
            exclusive: true,
            message: "Invalid file",
            test: (value) => {
              if (!value || value.length === 0) return true;

              for (const file of value) {
                if (
                  !validTypes.includes(file.type) &&
                  !validTypes.includes(file.mimeType)
                ) {
                  return new yup.ValidationError(
                    "Μη έγκυρος τύπος αρχείου",
                    null,
                    "file",
                  );
                }

                if (file.size > maxSize) {
                  return new yup.ValidationError(
                    `Το μέγεθος του αρχείου δεν πρέπει να υπερβαίνει τα ${maxSize / 1000000}MB`,
                    null,
                    "file",
                  );
                }
              }

              return true;
            },
          });
        }

        if (field.required) {
          fieldSchema = fieldSchema.required("Το πεδίο είναι υποχρεωτικό");
        } else {
          fieldSchema = fieldSchema.nullable();
        }

        if (field.validation === "custom" && field.regex) {
          fieldSchema = fieldSchema.matches(
            field.regex,
            field.validationMessage,
          );
        }

        acc[field.name] = fieldSchema.typeError(field.validationMessage);
      });
      return acc;
    }, {}),
  );

  const defaultValues = Object.keys(assetRequestFields).reduce((acc, key) => {
    assetRequestFields[key].forEach((field) => {
      acc[field.name] = null;
    });
    return acc;
  }, {});

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });

  const getFormFieldsByTab = () => {
    switch (selectedTab) {
      case -1:
        return assetRequestFields.general;
      case 0:
        return assetRequestFields.legal;
      case 1:
        return assetRequestFields.financial;
      case 2:
        return assetRequestFields.technical;
      case 3:
        return assetRequestFields.energy;
      default:
        return assetRequestFields.general;
    }
  };

  const getInputElementByFieldType = (field) => {
    if (
      field.type === "text" ||
      field.type === "number" ||
      field.type === "textarea"
    ) {
      return <CustomTextField {...field} control={control} />;
    } else if (field.type === "select" || field.type === "boolean") {
      const availableOptions =
        field.type === "boolean" ? ["Ναι", "Όχι"] : field.options;

      return (
        <DropdownField
          {...field}
          control={control}
          options={availableOptions}
          boolean={field.type === "boolean"}
        />
      );
    } else if (field.type === "date") {
      return <DateField {...field} control={control} />;
    } else if (field.type === "geoLocation") {
      return (
        <Controller
          name={field.name}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Box
              sx={{
                marginTop: 0.4,
              }}
            >
              <AutocompleteMap
                marker={value}
                error={!!error}
                onMarkerChange={onChange}
                readOnly={false}
                containerStyle={{
                  width: "100%",
                  height: "clamp(260px, 45vh, 400px)",
                }}
                inputFieldLabel={field.label}
                required={field.required}
              />
            </Box>
          )}
        />
      );
    } else if (field.type === "file") {
      return <AlfrescoFileUploader {...field} control={control} />;
    }
  };

  const handleSubmitValues = () => {
    const { generalFiles, complianceFiles, financialFiles, ...restValues } =
      getValues();
    const formattedValues = {
      ...restValues,
      generalFiles: generalFiles?.map((file) => ({
        ...file,
        groupTag: "General",
      })),
      complianceFiles: complianceFiles?.map((file) => ({
        ...file,
        groupTag: "Compliance",
      })),
      financialFiles: financialFiles?.map((file) => ({
        ...file,
        groupTag: "Financial",
      })),
    };
    onCreate(formattedValues);
  };

  return (
    <>
      <form onSubmit={handleSubmit(() => handleSubmitValues())}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Grid container spacing={2}>
            {getFormFieldsByTab()
              .filter((field) => field.display !== false)
              .map((field) => {
                if (
                  field.name === "floorCount" &&
                  nonFloorOptions.includes(selectedAssetCategory?.description)
                ) {
                  return null;
                }

                if (field.name === "geoLocation") {
                  return (
                    <Grid item xs={12} key={field.name}>
                      {getInputElementByFieldType(field)}
                    </Grid>
                  );
                } else if (field.type === "file") {
                  return (
                    <Grid item xs={12} key={field?.name}>
                      <Box width={{ xs: "100%", lg: "50%" }}>
                        {getInputElementByFieldType(field)}
                      </Box>
                    </Grid>
                  );
                } else {
                  return (
                    <Grid item xs={12} md={4} key={field?.name}>
                      {getInputElementByFieldType(field)}
                    </Grid>
                  );
                }
              })}
          </Grid>

          {Object.keys(errors).length > 0 && (
            <Typography color="error" fontSize={14}>
              Παρακαλούμε ελέγξτε ότι όλα τα πεδία έχουν συμπληρωθεί σωστά
            </Typography>
          )}

          <FormActions
            endActions={
              <SaveButton text="Δημιουργία" isLoading={isCreatingLoading} />
            }
          />
        </Box>
      </form>

      <ExitFormModal isFormDirty={isDirty} />
    </>
  );
};

AssetCreateForm.propTypes = {
  selectedTab: PropTypes.number.isRequired,
  onCreate: PropTypes.func,
  selectedAssetCategory: PropTypes.object,
};

export default AssetCreateForm;
